import React, { useState, useEffect } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import { profileService } from '../../services/profileService';
import { userService } from '../../services/userService';
import { User } from '../../types/users';
import { Profile } from '../../types/profiles';
import { useToast } from '../../components/UI/ToastProvider';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { useAuth } from '../../contexts/AuthContext';
import { canAssignAdministratorProfile, isAdministratorProfile } from '../../utils/ownerCustomerAccess';

export const ProfilesPage: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const customerId = sessionStorage.getItem('@KnowInDoc:customer_id') || undefined;
  const [search, setSearch] = useState('');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const pageSize = 10;

  // Estados para modal de adicionar usuários
  const [isAddUsersModalOpen, setIsAddUsersModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [addUserSearch, setAddUserSearch] = useState('');

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try
    {
      const data = await profileService.getProfiles(customerId);
      setProfiles(data);
    }
    catch (error)
    {
      console.error('Erro ao buscar perfis:', error);
      addToast('Não foi possível carregar os perfis.', 'error');
      setProfiles([]);
    }
  };

  const canAddUsersToProfile = (profile: Profile): boolean => {
    if (isAdministratorProfile(profile))
    {
      return canAssignAdministratorProfile(Boolean(user?.isOwnerCustomer));
    }

    return true;
  };

  const filteredProfiles = profiles.filter(profile =>
    profile.name.toLowerCase().includes(search.toLowerCase()) ||
    profile.description.toLowerCase().includes(search.toLowerCase())
  );

  const openUsersModal = async (profile: Profile) => {
    setSelectedProfile(profile);
    setIsModalOpen(true);
    await fetchUsers(profile.id, '', 1);
    setUserSearch('');
    setUserPage(1);
  };

  const fetchUsers = async (roleId: string, search: string, page: number) => {
    try {
      const data = await userService.getUsersByRole(roleId, search, page, pageSize);
      // Garantir que users seja sempre um array
      setUsers(Array.isArray(data.users) ? data.users : []);
      // Garantir que total seja sempre um número
      setUserTotal(typeof data.total === 'number' ? data.total : 0);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setUsers([]);
      setUserTotal(0);
    }
  };

  const handleUserSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserSearch(e.target.value);
    setUserPage(1);
    if (selectedProfile) {
      await fetchUsers(selectedProfile.id, e.target.value, 1);
    }
  };

  const handleUserPageChange = async (newPage: number) => {
    setUserPage(newPage);
    if (selectedProfile) {
      await fetchUsers(selectedProfile.id, userSearch, newPage);
    }
  };

  const openAddUsersModal = async (profile: Profile) => {
    if (!canAddUsersToProfile(profile))
    {
      addToast(
        'O perfil Administrador só pode receber usuários do cliente proprietário da plataforma.',
        'error'
      );
      return;
    }

    setSelectedProfile(profile);
    setIsAddUsersModalOpen(true);
    await fetchAvailableUsers(profile.id, '');
    setAddUserSearch('');
    setSelectedUsers([]);
  };

  const fetchAvailableUsers = async (roleId: string, search: string) => {
    try {
      // Buscar usuários que NÃO estão no perfil (disponíveis para adicionar)
      const data = await userService.getAvailableUsers(roleId, search);
      setAvailableUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      console.error('Erro ao buscar usuários disponíveis:', error);
      setAvailableUsers([]);
    }
  };

  const handleAddUserSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddUserSearch(e.target.value);
    if (selectedProfile) {
      await fetchAvailableUsers(selectedProfile.id, e.target.value);
    }
  };

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAddUsersToProfile = async () => {
    if (selectedUsers.length === 0 || !selectedProfile)
    {
      return;
    }

    if (!canAddUsersToProfile(selectedProfile))
    {
      addToast(
        'O perfil Administrador só pode receber usuários do cliente proprietário da plataforma.',
        'error'
      );
      return;
    }
    
    try {
      await userService.addUsersToRole(selectedProfile.id, selectedUsers);
      // Fechar modal e atualizar lista de usuários do perfil
      setIsAddUsersModalOpen(false);
      if (isModalOpen) {
        await fetchUsers(selectedProfile.id, userSearch, userPage);
      }
      setSelectedUsers([]);
    } catch (error) {
      console.error('Erro ao adicionar usuários ao perfil:', error);
    }
  };

  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gerenciamento de Perfis</h1>
      </div>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar perfis..."
              className="border rounded-md pl-10 pr-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-100">
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Nome do Perfil</th>
                <th className="py-3 px-4 text-left text-gray-600 font-medium">Descrição</th>
              </tr>
            </thead>
            <tbody>
              {filteredProfiles.map(profile => (
                <tr key={profile.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    {profile.name}
                  </td>
                  <td className="py-3 px-4">{profile.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Modal isOpen={isModalOpen} title={selectedProfile ? `Usuários do perfil: ${selectedProfile.name}` : ''} onClose={() => setIsModalOpen(false)}>
        <div className="mb-4 flex justify-between items-center">
          <input
            type="text"
            placeholder="Buscar usuários..."
            className="border rounded-md px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={userSearch}
            onChange={handleUserSearch}
          />
        </div>
        <div className="overflow-x-auto max-h-80">
          {users && users.length > 0 ? (
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">Nome</th>
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">E-mail</th>
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b">
                    <td className="py-2 px-4 flex items-center gap-2">
                      {user.avatarBase64 ? (
                        <img src={getAvatarUrl(user.avatarBase64)} alt={user.name} className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {(user.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      {user.name}
                    </td>
                    <td className="py-2 px-4">{user.email}</td>
                    <td className="py-2 px-4">
                      <span className={
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700'
                      + ' text-xs font-medium px-2 py-1 rounded-full'}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6M3 20h5v-2a4 4 0 013-3.87M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-gray-500 text-lg">Nenhum usuário localizado</p>
              <p className="text-gray-400 text-sm mt-1">Este perfil ainda não possui usuários associados</p>
            </div>
          )}
        </div>
        {users && users.length > 0 && (
          <div className="flex justify-between items-center mt-4">
            <span className="text-sm text-gray-600">
              Página {userPage} de {Math.ceil(userTotal / pageSize) || 1}
            </span>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                disabled={userPage === 1}
                onClick={() => handleUserPageChange(userPage - 1)}
              >
                Anterior
              </button>
              <button
                className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                disabled={userPage >= Math.ceil(userTotal / pageSize)}
                onClick={() => handleUserPageChange(userPage + 1)}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </Modal>
      <Modal isOpen={isAddUsersModalOpen} title={`Adicionar Usuários ao Perfil: ${selectedProfile?.name}`} onClose={() => setIsAddUsersModalOpen(false)}>
        <div className="mb-4 flex justify-between items-center">
          <input
            type="text"
            placeholder="Buscar usuários disponíveis..."
            className="border rounded-md px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={addUserSearch}
            onChange={handleAddUserSearch}
          />
        </div>
        <div className="overflow-x-auto max-h-80">
          {availableUsers && availableUsers.length > 0 ? (
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">Nome</th>
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">E-mail</th>
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">Status</th>
                  <th className="py-2 px-4 text-left text-gray-600 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {availableUsers.map(user => (
                  <tr key={user.id} className="border-b">
                    <td className="py-2 px-4 flex items-center gap-2">
                      {user.avatarBase64 ? (
                        <img src={getAvatarUrl(user.avatarBase64)} alt={user.name} className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-xs font-medium text-gray-600">
                            {(user.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      {user.name}
                    </td>
                    <td className="py-2 px-4">{user.email}</td>
                    <td className="py-2 px-4">
                      <span className={
                        user.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700'
                      + ' text-xs font-medium px-2 py-1 rounded-full'}>
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="py-2 px-4">
                      <button
                        className="p-1 rounded hover:bg-gray-100"
                        onClick={() => handleUserSelection(String(user.id))}
                        style={{
                          background: selectedUsers.includes(String(user.id)) ? '#4f46e5' : 'transparent',
                          color: selectedUsers.includes(String(user.id)) ? 'white' : 'currentColor',
                          border: selectedUsers.includes(String(user.id)) ? 'none' : '1px solid currentColor',
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6M3 20h5v-2a4 4 0 013-3.87M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-gray-500 text-lg">Nenhum usuário disponível para adicionar</p>
            </div>
          )}
        </div>
        {selectedUsers.length > 0 && (
          <div className="flex justify-end mt-4">
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
              onClick={handleAddUsersToProfile}
            >
              Adicionar Usuários
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}; 